import React, { useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import type { Invoice } from '../../../../shared/types';
import { InvoiceService } from '../../../services/invoiceService';
import { hasPermission } from '../../../../shared/permissions';
import { pageGate } from '../../../lib/pageGates';
import {
  WidgetFrame,
  WidgetActionButton,
  Stat,
  StatGroup,
  Meter,
  WChart,
  WList,
  type WListRow,
} from '../../../components/widgets';
import { fmtMoney, todayIso, shortDate, countChip, AddRow, useSettledFetch } from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

// ═══════════════════════════════════════════════════════════════
// INVOICES (Widgets R2 T2b) — hard-gated by the registry's
// `projects.view_financials`. The body's own soft check used to read a
// SEPARATE key (`invoices.view_financials`) that never reconciled with
// the registry gate (parity Finding 2) — reconciled here to the ONE
// registry key, so the `!canSeeMoney` amounts-hidden branch is deleted
// (it is unreachable once both checks agree).
// ═══════════════════════════════════════════════════════════════

const BUCKET_LABELS = [
  { key: 'current' as const, label: 'Current' },
  { key: 'd30' as const, label: '30+' },
  { key: 'd60' as const, label: '60+' },
  { key: 'd90' as const, label: '90+' },
];

const STATUS_LABEL: Record<Invoice['status'], string> = {
  draft: 'Draft',
  sent: 'Sent',
  partial: 'Partial',
  paid: 'Paid',
  void: 'Void',
};
const STATUS_COLOR: Record<Invoice['status'], string> = {
  draft: 'var(--color-text-subtle)',
  sent: 'var(--color-info)',
  partial: 'var(--color-warning)',
  paid: 'var(--color-success)',
  void: 'var(--color-danger)',
};

function InvoicesBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const canCreate = hasPermission(ctx.userPermissions, 'invoices.create') && !ctx.locked;
  // Project Canvas R1 (T3b, Decision 5/6): expand/create nest the real
  // Billing page via the project host — gated by the SAME predicate the
  // sidebar Billing nav item uses (`pageGate`), AND `view_financials` (the
  // widget's own registry gate) per the permissions checklist. No expand
  // control at all when the viewer lacks it — never an expand that opens
  // to a refusal.
  const canOpenInvoices = pageGate('invoices', ctx.userPermissions) && ctx.can.viewFinancials;
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const { status, reload } = useSettledFetch(
    () => [InvoiceService.list(ctx.project.id)],
    (results) => {
      const [invR] = results;
      if (invR.status === 'fulfilled') setInvoices(invR.value);
    },
    [ctx.project.id]
  );

  const { buckets, perBucketInvoices } = useMemo(() => {
    const today = new Date(todayIso());
    const buckets = { current: 0, d30: 0, d60: 0, d90: 0 };
    const perBucketInvoices: Record<'current' | 'd30' | 'd60' | 'd90', Invoice[]> = {
      current: [],
      d30: [],
      d60: [],
      d90: [],
    };
    for (const inv of invoices) {
      const invStatus = (inv.status || '') as string;
      if (!invStatus || invStatus === 'draft' || invStatus === 'paid' || invStatus === 'void')
        continue;
      const issued = (inv as any).issueDate || (inv as any).date;
      if (!issued) continue;
      const days = Math.floor((today.getTime() - new Date(issued).getTime()) / 86400000);
      const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
      if (open <= 0) continue;
      let bucket: 'current' | 'd30' | 'd60' | 'd90';
      if (days <= 30) bucket = 'current';
      else if (days <= 60) bucket = 'd30';
      else if (days <= 90) bucket = 'd60';
      else bucket = 'd90';
      buckets[bucket] += open;
      perBucketInvoices[bucket].push(inv);
    }
    return { buckets, perBucketInvoices };
  }, [invoices]);

  const total = buckets.current + buckets.d30 + buckets.d60 + buckets.d90;
  const overdueCount =
    perBucketInvoices.d30.length + perBucketInvoices.d60.length + perBucketInvoices.d90.length;

  const allOpenInvoices = [
    ...perBucketInvoices.d90,
    ...perBucketInvoices.d60,
    ...perBucketInvoices.d30,
    ...perBucketInvoices.current,
  ];

  const isEmpty = total === 0;
  const frameStatus =
    status === 'loading' ? 'loading' : status === 'error' ? 'error' : isEmpty ? 'empty' : 'ready';

  const openNewInvoice = () =>
    ctx.navigate?.({ view: 'invoices', projectId: ctx.project.id, create: true });
  const openBilling = () => ctx.navigate?.({ view: 'invoices', projectId: ctx.project.id });

  const chartData = BUCKET_LABELS.map((b) => ({ label: b.label, value: buckets[b.key] }));
  const meterRows = BUCKET_LABELS.map((b) => ({
    label: b.label,
    planned: total || 1,
    actual: buckets[b.key],
  }));

  const invoiceRows: WListRow[] = allOpenInvoices.map((inv) => {
    const issued = (inv as any).issueDate || (inv as any).date;
    const days = issued ? Math.floor((Date.now() - new Date(issued).getTime()) / 86400000) : 0;
    const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
    return {
      id: inv.id,
      primary: inv.invoiceNumber,
      meta: inv.dueDate
        ? `Due ${shortDate(inv.dueDate)}`
        : issued
          ? `${shortDate(issued)} · ${days}d`
          : '—',
      trailing: (
        <div className="flex flex-col items-end gap-1">
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--tx)' }}>
            {fmtMoney(open)}
          </span>
          <span style={countChip(STATUS_COLOR[inv.status])}>{STATUS_LABEL[inv.status]}</span>
        </div>
      ),
      onClick: () => ctx.onDrill({ kind: 'invoice', invoiceId: inv.id }),
    };
  });

  return (
    <WidgetFrame
      title="Invoices"
      status={frameStatus}
      form="stat"
      error={{ message: 'Could not load invoices.', onRetry: reload }}
      empty={{
        title: 'No outstanding receivables.',
        action:
          canCreate && canOpenInvoices
            ? { label: '+ New invoice', onClick: openNewInvoice }
            : undefined,
      }}
      actions={
        canOpenInvoices ? (
          <WidgetActionButton icon={Maximize2} label="Open Billing" onClick={openBilling} />
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <StatGroup>
          <Stat
            value={fmtMoney(total)}
            label="outstanding"
            compact
            onClick={() => ctx.onDrill({ kind: 'invoice-list', status: 'all' })}
          />
          <Stat
            value={String(overdueCount)}
            label="overdue"
            compact
            onClick={() => ctx.onDrill({ kind: 'invoice-list', status: 'overdue' })}
          />
        </StatGroup>
        <Meter
          label="Aging"
          planned={total || 1}
          actual={total}
          values={{ planned: fmtMoney(total), actual: fmtMoney(total), remaining: '' }}
          rows={meterRows}
        />
        <WChart
          kind="bar"
          data={chartData}
          series={[{ key: 'value', label: 'Outstanding', tone: 'info' }]}
          xKey="label"
          height={140}
        />
        <WList
          rows={invoiceRows}
          max={8}
          onViewAll={() => ctx.onDrill({ kind: 'invoice-list', status: 'all' })}
          footer={
            canCreate && canOpenInvoices ? (
              <AddRow label="+ New invoice" onClick={openNewInvoice} />
            ) : undefined
          }
        />
      </div>
    </WidgetFrame>
  );
}

export const invoicesWidget: ProjectWidgetDef = {
  id: 'invoices',
  title: 'Invoices',
  description:
    'Outstanding receivables bucketed by age. Expanded view lists invoices with click-through.',
  category: 'financial',
  requires: ['projects.view_financials'],
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  inputSurface: false,
  render: (ctx) => <InvoicesBody ctx={ctx} />,
};
